<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import { AccountDetails } from '@/types'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, handleAxiosError, postcodeMessageKey } from '@/composables/useAxiosError'
import { useI18n } from 'vue-i18n'
import { useAccountFormState, useDirtyFormGuard, LOADING_SIZES, LOADING_WIDTH } from '@/composables'
import { useLocale } from '@/composables/useLocale'
import { useAccountPricingStore } from '@/stores/accountPricing'

// Sub-components
import AccountPersonalInfo from './sections/AccountPersonalInfo.vue'
import AccountAddressInfo from './sections/AccountAddressInfo.vue'
import AccountContactInfo from './sections/AccountContactInfo.vue'
import AccountEmailChangeModal from './sections/AccountEmailChangeModal.vue'

import type { PersonalInfoData } from './sections/AccountPersonalInfo.vue'
import type { AddressInfoData } from './sections/AccountAddressInfo.vue'
import type { ContactInfoData } from './sections/AccountContactInfo.vue'

const props = defineProps<{
  accountDetails: AccountDetails
}>()

// Locale management
const { setLocale } = useLocale()

// Form state management
const {
  formData,
  isEditing,
  saving,
  isDirty,
  error: updateFailed,
  success: updateSuccess,
  activateForm,
  revokeChanges,
  markAsSaving,
  markAsSaved,
  markAsFailed
} = useAccountFormState(toRef(props, 'accountDetails'))

// Register dirty form guard for navigation warnings
useDirtyFormGuard('account-stammdaten', isDirty)

// Email change modal state
const showEmailChangeModal = ref(false)

// Track address country for phone sync
const addressCountryCode = ref<string>('')

// Computed props for sub-components
const isPerson = computed(() => formData.value.account_type === 'person')

const personalInfo = computed<PersonalInfoData>({
  get: () => ({
    name: formData.value.name || '',
    surname: formData.value.surname || '',
    org: formData.value.org || '',
    account_type: (formData.value.account_type as 'person' | 'organization') || 'person'
  }),
  set: (value) => {
    formData.value.name = value.name
    formData.value.surname = value.surname
    formData.value.org = value.org
  }
})

const addressInfo = computed<AddressInfoData>({
  get: () => ({
    line1: formData.value.line1 || '',
    line2: formData.value.line2 || '',
    postcode: formData.value.postcode || '',
    city: formData.value.city || '',
    country_iso: formData.value.country_iso || 'de',
    state: formData.value.state
  }),
  set: (value) => {
    formData.value.line1 = value.line1
    formData.value.line2 = value.line2
    formData.value.postcode = value.postcode
    formData.value.city = value.city
    formData.value.country_iso = value.country_iso
    formData.value.state = value.state
  }
})

const contactInfo = computed<ContactInfoData>({
  get: () => ({
    phone: formData.value.phone || '',
    email: formData.value.email || '',
    lang: (formData.value.lang as ContactInfoData['lang']) || 'de',
    ust_id: formData.value.ust_id || ''
  }),
  set: (value) => {
    formData.value.phone = value.phone
    formData.value.lang = value.lang as 'de' | 'en'
    formData.value.ust_id = value.ust_id
  }
})

// Handle country change from address component
const handleCountryChanged = (countryCode: string) => {
  addressCountryCode.value = countryCode
}

const { t } = useI18n()

// Specific save-error message (e.g. postcode validation); empty = generic
const saveErrorDetail = ref('')

// Save form
const saveForm = async () => {
  markAsSaving()
  saveErrorDetail.value = ''

  try {
    const res = await useAxios<AccountDetails, null>('POST', '/account', true, formData.value)

    if (!res) {
      markAsFailed()
      return
    }

    if (res.status === STATUS_CODES.OK) {
      markAsSaved()
      // The VAT ID and the address decide the pricing basis.
      useAccountPricingStore().invalidate()
      // Sync locale with saved language preference
      if (formData.value.lang) {
        setLocale(formData.value.lang)
      }
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AccountPageDetails' })
    const key = postcodeMessageKey(getBackendErrorCode(err))
    if (key) {
      saveErrorDetail.value = t(key)
    }
    markAsFailed()
  }
}

// Open email change modal
const openEmailChangeModal = () => {
  showEmailChangeModal.value = true
}
</script>

<template>
  <div class="details-page">
    <!-- Alert Messages -->
    <Transition name="slide-fade">
      <div v-if="updateFailed" class="alert alert-error">
        <v-icon icon="mdi-alert-circle" size="20" />
        <span>{{ saveErrorDetail || $t('dashboard.account.stammdaten.saveFailed') }}</span>
      </div>
    </Transition>

    <Transition name="slide-fade">
      <div v-if="updateSuccess" class="alert alert-success">
        <v-icon icon="mdi-check-circle" size="20" />
        <span>{{ $t('dashboard.account.stammdaten.saveSuccess') }}</span>
      </div>
    </Transition>

    <!-- Section Header -->
    <div class="section-header">
      <div class="section-info">
        <h2 class="section-title">{{ $t('dashboard.account.stammdaten.title') }}</h2>
        <p class="section-description">{{ $t('dashboard.account.stammdaten.description') }}</p>
      </div>
      <div v-if="!isEditing" class="section-actions">
        <button class="btn btn-primary" @click="activateForm">
          <v-icon icon="mdi-pencil" size="18" />
          <span>{{ $t('dashboard.account.stammdaten.edit') }}</span>
        </button>
      </div>
    </div>

    <!-- Form Content -->
    <form class="form-content" @submit.prevent="saveForm">
      <!-- Personal Info Section -->
      <AccountPersonalInfo v-model="personalInfo" :disabled="!isEditing" />

      <!-- Address Section -->
      <AccountAddressInfo
        v-model="addressInfo"
        :disabled="!isEditing"
        @country-changed="handleCountryChanged"
      />

      <!-- Contact Section -->
      <AccountContactInfo
        v-model="contactInfo"
        :disabled="!isEditing"
        :is-person="isPerson"
        :address-country-code="addressCountryCode"
        @open-email-change="openEmailChangeModal"
      />

      <!-- Action Buttons -->
      <div v-if="isEditing" class="form-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="revokeChanges">
          {{ $t('dashboard.account.stammdaten.cancel') }}
        </button>
        <button type="submit" class="btn btn-success" :disabled="saving">
          <v-progress-circular
            v-if="saving"
            indeterminate
            :size="LOADING_SIZES.button"
            :width="LOADING_WIDTH"
          />
          <v-icon v-else icon="mdi-check" size="18" />
          <span>{{
            saving
              ? $t('dashboard.account.stammdaten.saving')
              : $t('dashboard.account.stammdaten.save')
          }}</span>
        </button>
      </div>
    </form>

    <!-- Email Change Modal -->
    <AccountEmailChangeModal
      v-model="showEmailChangeModal"
      :current-email="formData.email || ''"
    />
  </div>
</template>

<style lang="scss" scoped>
.details-page {
  width: 100%;
}

/* Alerts */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 0.9rem;
  font-weight: 500;
}

.alert-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  color: #ef5350;
}

.alert-success {
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.section-description {
  font-size: 0.875rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: rgba(0, 176, 255, 0.15);
  color: #00b0ff;
  border: 1px solid rgba(0, 176, 255, 0.3);

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.25);
    box-shadow: 0 0 20px rgba(0, 176, 255, 0.15);
  }
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(200, 218, 255, 0.7);
  border: 1px solid rgba(120, 163, 255, 0.15);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.btn-success {
  background: rgba(76, 175, 80, 0.15);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.3);

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.15);
  }
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Light Theme */
.v-theme--lightTheme {
  .section-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-description {
    color: rgba(15, 23, 42, 0.6);
  }

  .btn-primary {
    background: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.2);

    &:hover:not(:disabled) {
      background: rgba(28, 110, 227, 0.15);
    }
  }

  .btn-secondary {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(15, 23, 42, 0.7);
    border-color: rgba(28, 110, 227, 0.15);
  }

  .form-actions {
    border-color: rgba(28, 110, 227, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    gap: 16px;
  }

  .form-actions {
    flex-direction: column;
    gap: 10px;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .btn,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
